import { Injectable, InjectionToken, Provider, inject } from '@angular/core';

import { ProductCounterKey, ProductKey } from '@app/domain/models/product-selection.model';
import {
  PRODUCT_COUNTS_CACHE_TTL_MS,
  ProductCountsCache,
  ProductCountsCacheLists,
} from '@app/domain/ports/product-counts-cache.port';

/** Reloj de la caché. Inyectable para que los specs no dependan de la hora real. */
export const PRODUCT_COUNTS_CACHE_CLOCK = new InjectionToken<() => number>('PRODUCT_COUNTS_CACHE_CLOCK', {
  providedIn: 'root',
  factory: () => () => Date.now(),
});

interface Entrada<T> {
  readonly valor: T;
  readonly guardadoEn: number;
}

/**
 * Los conteos de `/products` en la memoria de la PESTAÑA (feature 152).
 *
 * - Singleton raíz: sobrevive a salir de `/products` y volver (el navbar lleva
 *   de vuelta), que es justo cuando no hay que repetir la descarga.
 * - ⚠️ Solo heap: nada va a `sessionStorage`, `localStorage`, la URL ni a logs.
 *   Se vacía en `LogoutService.forgetTabMemory()` (cierre de sesión e inicio de
 *   login), así que el siguiente usuario de la pestaña no hereda nada.
 */
@Injectable({ providedIn: 'root' })
export class InMemoryProductCountsCache extends ProductCountsCache {
  private readonly ahora = inject(PRODUCT_COUNTS_CACHE_CLOCK);
  private readonly conteos = new Map<string, Entrada<number>>();
  private readonly listas = new Map<keyof ProductCountsCacheLists, Entrada<unknown>>();

  get(product: ProductKey, counter: ProductCounterKey): number | null {
    return this.vigente(this.conteos, `${product}#${counter}`);
  }

  set(product: ProductKey, counter: ProductCounterKey, value: number): void {
    // Solo un número válido es un éxito; cualquier otra cosa no se guarda (R9).
    if (!Number.isInteger(value) || value < 0) return;
    this.conteos.set(`${product}#${counter}`, { valor: value, guardadoEn: this.ahora() });
  }

  getList<K extends keyof ProductCountsCacheLists>(key: K): ProductCountsCacheLists[K] | null {
    return this.vigente(this.listas, key) as ProductCountsCacheLists[K] | null;
  }

  setList<K extends keyof ProductCountsCacheLists>(key: K, list: ProductCountsCacheLists[K]): void {
    this.listas.set(key, { valor: list, guardadoEn: this.ahora() });
  }

  clear(): void {
    this.conteos.clear();
    this.listas.clear();
  }

  private vigente<K, T>(mapa: Map<K, Entrada<T>>, llave: K): T | null {
    const entrada = mapa.get(llave);
    if (!entrada) return null;
    if (this.ahora() - entrada.guardadoEn >= PRODUCT_COUNTS_CACHE_TTL_MS) {
      mapa.delete(llave);
      return null;
    }
    return entrada.valor;
  }
}

/**
 * El enlace del puerto con el singleton de la pestaña. Va SOLO en la raíz
 * (`app.config.ts`, y en los TestBed como raíz). ⚠️ Nunca en `providers` de una
 * página: la re-crearía con cada montaje y no cachearía nada.
 */
export function provideProductCountsCache(): Provider[] {
  return [{ provide: ProductCountsCache, useExisting: InMemoryProductCountsCache }];
}
